// SS PWA — components/diagnostics/AnomalyFeed.tsx — 2026-06-03
//
// Operator-only anomaly feed. Renders the raw rule names — explicitly
// off-limits for client view per spec.

import type { AnomalyEvidence } from '@/lib/api/diagnostics';

interface AnomalyFeedProps {
  anomalies: AnomalyEvidence[];
  /** Optional callback when operator clicks a row. */
  onClickRow?: (a: AnomalyEvidence) => void;
}

function tone(sev: AnomalyEvidence['severity']): string {
  if (sev === 'P0') return 'bg-red-100 text-red-800 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900';
  if (sev === 'P1') return 'bg-orange-100 text-orange-800 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:ring-orange-900';
  if (sev === 'P2') return 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900';
  return 'bg-ink-100 text-ink-700 ring-ink-200 dark:bg-[#1F2A37] dark:text-[#94A3B8] dark:ring-[#2A374A]';
}

export function AnomalyFeed({ anomalies, onClickRow }: AnomalyFeedProps) {
  if (anomalies.length === 0) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
        No anomalies in window.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-ink-100 rounded-2xl border border-ink-200 bg-white dark:divide-[#1F2A37] dark:border-[#1F2A37] dark:bg-[#0F172A]">
      {anomalies.map((a, i) => (
        <li key={`${a.rule}-${i}`}>
          <button
            type="button"
            className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-ink-50 dark:hover:bg-[#1F2A37]"
            onClick={() => onClickRow?.(a)}
          >
            <span className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ring-1 ring-inset ${tone(a.severity)}`}>
              {a.severity}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate font-mono text-xs text-ink-900 dark:text-white">{a.rule}</div>
              <div className="truncate text-xs text-ink-500 dark:text-[#94A3B8]">
                {Object.entries(a.evidence)
                  .slice(0, 4)
                  .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                  .join(' · ')}
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}
